<template>
  <Component
    :is="selectedView"
    :count="count"
    :fetchingStatus="fetchingStatus"
    v-bind="!maxSmall ? { isSelectable } : {}"
    @show:detail="emit('show:detail', $event)"
    @update:count="$emit('update:count', $event)"
    @update:fetchingStatus="emit('update:fetchingStatus', $event)"
  >
    <template v-slot:state v-if="count !== null || fetchingStatus === 'error'">
      <State
        :type="state.type"
        :title="state.title"
        :text="state.text"
        :button="state.button"
        :action="state.action"
        @click:button="handleStateEmit(state.emit)"
      />
    </template>
    <template #bulk-actions="{ selectedFiles }">
      <FilesBulkActions :selectedFiles="selectedFiles" />
    </template>
  </Component>
</template>

<script setup>
// Import Vue Utilites
import { computed, inject } from "vue";

// Import Utilities
import { storeToRefs } from "pinia";
import { usePage } from "@inertiajs/vue3";
import { useI18n } from "vue-i18n";

// Import Components
import FilesDataGrid from "./FilesDataGrid.vue";
import FilesList from "./FilesList.vue";
import { State } from "@/SharedComponents/State";
import FilesBulkActions from "@/Pages/Files/FilesBulkActions";

// Composables
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useSearch } from "@/Composables/useSearch";
import { useFilters } from "@/Composables/useFilters";

// Emits
const emit = defineEmits([
  "show:detail",
  "update:count",
  "update:fetchingStatus",
  "add:file",
  "reset",
  "reload",
]);

// Search: Files
const searchStore = useSearch("files");
const { query } = storeToRefs(searchStore);

// Filters: Files
const filterStore = useFilters("files");
const { isFiltered } = storeToRefs(filterStore);

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// Breakpoints
const { maxSmall } = useBreakpoints();

// Translations
const { t } = useI18n();

// Define Props
const props = defineProps({
  count: { type: Number, default: 0 },
  fetchingStatus: { type: String, required: true },
});

// Computed props
const selectedView = computed(() => {
  return maxSmall.value ? FilesList : FilesDataGrid;
});

const state = computed(() => {
  let type = "Files";
  let title = t("components.state.no_files.title");
  let text = t("components.state.no_files.text");
  let button = t("components.state.no_files.button");
  let emit = "add:file";
  let action = "add-file";

  if (query.value || isFiltered.value) {
    type = "NoResults";
    title = t("components.state.no_results.title");
    text = t("components.state.no_results.text");
    button = t("components.state.no_results.button");
    emit = "reset";
    action = "reset";
  }

  if (props.fetchingStatus === "error") {
    type = "Error";
    title = t("components.state.error.title");
    text = t("components.state.error.text");
    button = t("components.state.error.button");
    emit = "reload";
    action = "reload";
  }

  return {
    type: type,
    title: title,
    text: text,
    button: button,
    emit: emit,
    action: action,
  };
});

const { hasPermission, permissions, tenantsWithUserPermission } =
  inject("permissions");

const isSelectable = computed(() => canEditFiles.value || canDeleteFiles.value);

const canEditFiles = computed(() => {
  if (globalFilterValues.value.tenant?.length > 0) {
    return hasPermission(
      permissions.updateDocument,
      globalFilterValues.value.tenant[0]
    );
  }
  return (
    tenantsWithUserPermission(permissions.updateDocument).length ===
    usePage().props.auth.permissions.length
  );
});

const canDeleteFiles = computed(() => {
  if (globalFilterValues.value.tenant?.length > 0) {
    return hasPermission(
      permissions.deleteDocument,
      globalFilterValues.value.tenant[0]
    );
  }
  return tenantsWithUserPermission(permissions.deleteDocument).length > 0;
});

// Functions
const handleStateEmit = (value) => {
  emit(value);
};
</script>
